import { useState, useRef, useCallback } from 'react'
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react'
import {
  useSectionPhoto,
  useUpdateSectionPhoto,
  useDeleteSectionPhoto,
} from '@/hooks/useCanvasArtifacts'
import type { CanvasSection } from './canvasTemplates'

interface PhotoSectionCardProps {
  section: CanvasSection
  canvasInstanceUri: string | null
}

export function PhotoSectionCard({ section, canvasInstanceUri }: PhotoSectionCardProps) {
  const Icon = section.icon
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Fetch existing photo
  const { data: photoUrl, isLoading } = useSectionPhoto(canvasInstanceUri, section.id)

  // Upload photo mutation
  const uploadMutation = useUpdateSectionPhoto()

  // Delete photo mutation
  const deleteMutation = useDeleteSectionPhoto()

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
      }
      // Limit file size to 2MB for base64 storage
      if (file.size > 2 * 1024 * 1024) {
        alert('Image must be less than 2MB')
        return
      }
      if (!canvasInstanceUri) return

      // Convert to base64 data URL
      const dataUrl = await fileToDataUrl(file)
      uploadMutation.mutate({
        canvasUri: canvasInstanceUri,
        sectionId: section.id,
        photoUrl: dataUrl,
      })
    },
    [canvasInstanceUri, section.id, uploadMutation]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFileSelect(file)
    },
    [handleFileSelect]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileSelect(file)
    // Reset input
    e.target.value = ''
  }

  const isUploading = uploadMutation.isPending
  const isDeleting = deleteMutation.isPending
  const hasPhoto = !!photoUrl

  return (
    <div
      style={{
        gridArea: section.gridArea,
        backgroundColor: section.bgColor,
        borderRadius: '12px',
        border: `2px solid ${section.color}20`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'border-color 0.2s',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 14px',
          borderBottom: `1px solid ${section.color}20`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexShrink: 0,
        }}
      >
        <Icon style={{ width: '18px', height: '18px', color: section.color, flexShrink: 0 }} />
        <span style={{ flex: 1, fontWeight: 600, fontSize: '13px', color: section.color }}>
          {section.title}
        </span>
        {hasPhoto && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (confirm('Remove photo?') && canvasInstanceUri) {
                deleteMutation.mutate({
                  canvasUri: canvasInstanceUri,
                  sectionId: section.id,
                })
              }
            }}
            disabled={isDeleting}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${section.color}40`,
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isDeleting ? 'wait' : 'pointer',
              opacity: 0.7,
              transition: 'opacity 0.15s',
            }}
            title="Remove photo"
          >
            {isDeleting ? (
              <Loader2 style={{ width: '14px', height: '14px', color: section.color, animation: 'spin 1s linear infinite' }} />
            ) : (
              <X style={{ width: '14px', height: '14px', color: section.color }} />
            )}
          </button>
        )}
      </div>

      {/* Content - Photo display or upload area */}
      <div
        style={{
          flex: 1,
          minHeight: 0, // Critical for flex children to shrink properly
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          position: 'relative',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />

        {isLoading ? (
          <Loader2
            style={{
              width: '32px',
              height: '32px',
              color: section.color,
              animation: 'spin 1s linear infinite',
            }}
          />
        ) : hasPhoto ? (
          // Display photo
          <div
            onClick={handleClick}
            style={{
              position: 'absolute',
              inset: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            title="Click to change photo"
          >
            <img
              src={photoUrl}
              alt="Persona"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </div>
        ) : canvasInstanceUri ? (
          // Upload area
          <div
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: isUploading ? 'wait' : 'pointer',
              borderRadius: '8px',
              border: `2px dashed ${isDragging ? section.color : section.color + '40'}`,
              backgroundColor: isDragging ? section.color + '10' : 'transparent',
              transition: 'all 0.2s',
            }}
          >
            {isUploading ? (
              <Loader2
                style={{
                  width: '32px',
                  height: '32px',
                  color: section.color,
                  animation: 'spin 1s linear infinite',
                }}
              />
            ) : (
              <>
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: section.color + '20',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isDragging ? (
                    <Upload style={{ width: '28px', height: '28px', color: section.color }} />
                  ) : (
                    <ImageIcon style={{ width: '28px', height: '28px', color: section.color }} />
                  )}
                </div>
                <span style={{ fontSize: '11px', color: section.color, textAlign: 'center' }}>
                  {isDragging ? 'Drop image here' : 'Click or drag to upload'}
                </span>
              </>
            )}
          </div>
        ) : (
          // No canvas instance selected
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: 0.5,
            }}
          >
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: section.color + '20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ImageIcon style={{ width: '28px', height: '28px', color: section.color }} />
            </div>
            <span style={{ fontSize: '11px', color: section.color, textAlign: 'center' }}>
              Select a canvas instance to upload photo
            </span>
          </div>
        )}
      </div>

      {/* Description */}
      <div
        style={{
          padding: '8px 12px',
          borderTop: `1px solid ${section.color}20`,
          backgroundColor: 'white',
        }}
      >
        <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          {section.description}
        </p>
      </div>
    </div>
  )
}

// Helper to convert File to data URL
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
